import { useState, useEffect } from 'react';

import useProductCardOptions from 'data-hooks/useProductCardOptions';
import useProductBasicDetails from 'data-hooks/useProductBasicDetails';
import { findAvailabilityAndVisibility } from 'data-hooks/utils';
import isServer from 'commons/isServer';
import {
  STOCK_VALUE_STATES,
  product_item_availability,
} from 'commons/constants';

const isOptionValueInOptions = (options, forMaterial, forMaterialType) => {
  const materialOption = options.find(
    optVal => optVal.name === forMaterialType
  );
  if (
    !materialOption ||
    !materialOption.values.some(opt => opt.value === forMaterial)
  ) {
    return false;
  }
  return true;
};

/**
 *  Used by product cards to determine if product is out of stock (to display OOS label)
 *  or discontinued (to remove from display) based on stock response.
 *  Includes both EFG internal and FFG.
 *
 *  Returns object of shape
 *  {
 *    isProductOOS,
 *    isProductDiscontinued,
 *    isAdditionalVisProductAvailable,
 *    isMarkedOOSOrDiscontinued,
 *    isEFGInternalOOS
 *  }
 */

const useProductAvailabilityByStock = (id, slug, hookOptions = {}) => {
  const {
    isAddlVisProductSku,
    forMaterial,
    forMaterialType,
    layout,
    localQSStock,
    productSku,
    delayLoad = false,
    stockDataFromParentLoading = false,
    allowDiscontinuedRemoval = false,
    hasStockData = true,
  } = hookOptions;

  const {
    data: basicDetailsData,
    loading: basicDetailsLoading,
  } = useProductBasicDetails(id, null, {
    skipPDPStateHook: true,
    productSku,
    delayLoad: isServer() || delayLoad,
  });
  const { loading, data: optionsData } = useProductCardOptions(id, slug, {
    skipLocalStockHook: false,
    localStockHookOptions: {
      hasStockData,
      localQSStock,
      stockDataFromParentLoading,
    },
    productSku,
    delayLoad: isServer() || delayLoad,
  });
  const [
    isAdditionalVisProductAvailable,
    setIsAdditionalVisProductAvailable,
  ] = useState(true);
  const [stockState, setStockState] = useState(STOCK_VALUE_STATES.LOADING);
  const [isProductOOS, setIsProductOOS] = useState(false);
  const [isProductDiscontinued, setIsProductDiscontinued] = useState(false);
  const [isMarkedOOSOrDiscontinued, setIsMarkedOOSOrDiscontinued] = useState(
    false
  );
  const [isEFGInternalOOS, setIsEFGInternalOOS] = useState(false);

  useEffect(() => {
    if (
      !optionsData?.options ||
      !forMaterial ||
      !forMaterialType ||
      !isAddlVisProductSku ||
      layout !== 'favorite' ||
      !basicDetailsData ||
      (basicDetailsData?.efg_flag && !basicDetailsData?.is_external)
    ) {
      return;
    }

    if (!optionsData.options?.length && forMaterial && forMaterialType) {
      setIsAdditionalVisProductAvailable(false);
      return;
    }

    setIsAdditionalVisProductAvailable(
      isOptionValueInOptions(optionsData.options, forMaterial, forMaterialType)
    );
  }, [
    basicDetailsData,
    forMaterial,
    forMaterialType,
    isAddlVisProductSku,
    layout,
    optionsData?.options,
  ]);

  useEffect(() => {
    if (
      !basicDetailsData ||
      basicDetailsLoading ||
      !optionsData?.stock ||
      !!optionsData?.stock?.loading
    ) {
      return;
    }
    const { stockOptions = [], selectedOptionsStockQty = 0 } =
      optionsData.stock || {};
    const isEFGExternal =
      basicDetailsData.efg_flag && basicDetailsData.is_external;
    let isOutOfStock = false;
    const { availability } = findAvailabilityAndVisibility(
      basicDetailsData.attributes
    );
    if (
      `${availability}` === `${product_item_availability.out_of_stock}` ||
      `${availability}` === `${product_item_availability.disabled}`
    ) {
      isOutOfStock = true;
    }

    if (
      forMaterial &&
      forMaterialType &&
      isAddlVisProductSku &&
      layout === 'favorite'
    ) {
      const isVisible = isOptionValueInOptions(
        stockOptions,
        forMaterial,
        forMaterialType
      );
      setIsAdditionalVisProductAvailable(isVisible);
      return;
    }

    // If product is not set as OOS or discontinued return early
    if (!isOutOfStock && !basicDetailsData.discontinued) {
      return;
    }
    setIsMarkedOOSOrDiscontinued(true);
    if (isEFGExternal) {
      if (basicDetailsData.discontinued) {
        setStockState(STOCK_VALUE_STATES.DISCONTINUED);
        setIsProductDiscontinued(true);
        return;
      }
      if (isOutOfStock) {
        setStockState(STOCK_VALUE_STATES.OOS);
        setIsProductOOS(true);
      }
      return;
    }
    if (
      !stockOptions.length &&
      !selectedOptionsStockQty &&
      !isAddlVisProductSku &&
      (layout === 'category' || allowDiscontinuedRemoval)
    ) {
      if (basicDetailsData.discontinued) {
        setStockState(STOCK_VALUE_STATES.DISCONTINUED);
        setIsProductDiscontinued(true);
        return;
      }
      if (isOutOfStock) {
        setStockState(STOCK_VALUE_STATES.OOS);
        setIsProductOOS(true);
      }
      return;
    }
    if (
      stockState === STOCK_VALUE_STATES.LOADING &&
      (!!stockOptions.length || !!selectedOptionsStockQty)
    ) {
      if (basicDetailsData.discontinued) {
        setStockState(STOCK_VALUE_STATES.INSTOCK);
        setIsProductDiscontinued(false);
      }
      if (isOutOfStock) {
        setIsProductOOS(false);
      }
    }
  }, [
    allowDiscontinuedRemoval,
    basicDetailsData,
    basicDetailsLoading,
    forMaterial,
    forMaterialType,
    isAddlVisProductSku,
    layout,
    optionsData?.stock,
    stockState,
  ]);

  useEffect(() => {
    if (
      !basicDetailsData ||
      basicDetailsLoading ||
      !optionsData?.stock ||
      !!optionsData?.stock?.loading ||
      !basicDetailsData.efg_flag ||
      basicDetailsData?.is_external ||
      isMarkedOOSOrDiscontinued ||
      forMaterial ||
      forMaterialType ||
      isAddlVisProductSku
    ) {
      return;
    }

    const { stockOptions = [], selectedOptionsStockQty = 0 } =
      optionsData.stock || {};

    if (
      !stockOptions.length &&
      !selectedOptionsStockQty &&
      (layout === 'category' || allowDiscontinuedRemoval) &&
      !isEFGInternalOOS
    ) {
      setStockState(STOCK_VALUE_STATES.OOS);
      setIsEFGInternalOOS(true);
      return;
    }

    if (
      stockOptions.length ||
      (selectedOptionsStockQty &&
        stockState === STOCK_VALUE_STATES.LOADING &&
        isEFGInternalOOS)
    ) {
      setStockState(STOCK_VALUE_STATES.INSTOCK);
      setIsEFGInternalOOS(false);
    }
  }, [
    allowDiscontinuedRemoval,
    basicDetailsData,
    basicDetailsLoading,
    forMaterial,
    forMaterialType,
    isAddlVisProductSku,
    isEFGInternalOOS,
    isMarkedOOSOrDiscontinued,
    layout,
    optionsData?.stock,
    stockState,
  ]);

  return {
    loading,
    isAdditionalVisProductAvailable,
    isProductOOS,
    isProductDiscontinued,
    isMarkedOOSOrDiscontinued,
    isEFGInternalOOS,
    isEFGInternal: !!(
      basicDetailsData?.efg_flag && !basicDetailsData?.is_external
    ),
  };
};

export default useProductAvailabilityByStock;
